import { FC, useContext, useEffect } from "react";
import { Controller } from "react-hook-form";
import FormFieldsContainer from "../../../../components/ui/FormFieldsContainer";
import { Form, FormField, Input, ModalDialog, Numeric, Switch } from "@rothoblaas/design-system";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../context/notificationContext";
import useI18n from "../../../../hooks/useTranslations";
import { type InsertTimberBody, type UpdateTimberBody } from "../../../../http/beamReinforcement/types/timber";
import { type TimberClass } from "../../../../http/beamReinforcement/types/timberClass";
import { useBRTimbersApi } from "../../../../http/beamReinforcement/useAdminTimbers";

type TimberFormBody = {
  name: string | null;
  density: number | null;
  bendingStrength: number | null;
  shearStrength: number | null;
  modulusOfElasticity: number | null;
  isGlulam: boolean;
};

const TimberClassFormDialog: FC<{ timber?: TimberClass; onClose: () => void }> = ({ timber, onClose }) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const timbersApi = useBRTimbersApi();
  const t = i18n.translation.beamReinforcementModule.timbers;

  const defaultValues: TimberFormBody = {
    name: timber?.name ?? null,
    density: timber?.density ?? null,
    bendingStrength: timber?.bendingStrength ?? null,
    shearStrength: timber?.shearStrength ?? null,
    modulusOfElasticity: timber?.modulusOfElasticity ?? null,
    isGlulam: timber?.isGlulam ?? false,
  };

  const onSubmit = () => async (formData: TimberFormBody) => {
    if (
      formData.name == null ||
      formData.density == null ||
      formData.bendingStrength == null ||
      formData.shearStrength == null ||
      formData.modulusOfElasticity == null
    ) {
      return;
    }

    const body: InsertTimberBody | UpdateTimberBody = {
      name: formData.name,
      density: formData.density,
      bendingStrength: formData.bendingStrength,
      shearStrength: formData.shearStrength,
      modulusOfElasticity: formData.modulusOfElasticity,
      isGlulam: formData.isGlulam,
    };

    try {
      if (timber) {
        await timbersApi.updateBRTimber(timber.name, body);
      } else {
        await timbersApi.insertBRTimber(body);
      }
      dispatch(showSuccessNotification(i18n.translation));
      onClose();
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  return (
    <ModalDialog
      onClose={onClose}
      title={timber ? `${i18n.translation.common.edit} ${t.singular}` : `${i18n.translation.common.add} ${t.singular}`}
    >
      <Form<TimberFormBody>
        submitText={i18n.translation.common.save}
        onSubmit={onSubmit}
       
        defaultValues={defaultValues}
      >
        {({ control, register, formState: { errors }, reset }) => {
          useEffect(() => {
            reset(defaultValues);
          }, [timber]);

          return (
            <FormFieldsContainer fullWidth>
              <FormField label={i18n.translation.common.name} error={errors.name}>
                {({ labelId, isInvalid }) => (
                  <Input
                    id={labelId}
                    isInvalid={isInvalid}
                    {...register("name", {
                      required: true,
                      minLength: 1,
                    })}
                  />
                )}
              </FormField>
              <Controller
                name="density"
                control={control}
                rules={{ min: 0.001 }}
                render={({ field, fieldState }) => (
                  <FormField label={t.fields.density} error={fieldState.error}>
                    {({ labelId, isInvalid }) => (
                      <Numeric
                        id={labelId}
                        value={field.value ?? undefined}
                        min={0.001}
                        decimalScale={3}
                        isInvalid={isInvalid}
                        onValueChange={(value) => field.onChange(value)}
                      />
                    )}
                  </FormField>
                )}
              />
              <Controller
                name="bendingStrength"
                control={control}
                rules={{ min: 0.001 }}
                render={({ field, fieldState }) => (
                  <FormField label={t.fields.bendingStrength} error={fieldState.error}>
                    {({ labelId, isInvalid }) => (
                      <Numeric
                        id={labelId}
                        value={field.value ?? undefined}
                        min={0.001}
                        decimalScale={3}
                        isInvalid={isInvalid}
                        onValueChange={(value) => field.onChange(value)}
                      />
                    )}
                  </FormField>
                )}
              />
              <Controller
                name="shearStrength"
                control={control}
                rules={{ min: 0.001 }}
                render={({ field, fieldState }) => (
                  <FormField label={t.fields.shearStrength} error={fieldState.error}>
                    {({ labelId, isInvalid }) => (
                      <Numeric
                        id={labelId}
                        value={field.value ?? undefined}
                        min={0.001}
                        decimalScale={3}
                        isInvalid={isInvalid}
                        onValueChange={(value) => field.onChange(value)}
                      />
                    )}
                  </FormField>
                )}
              />
              <Controller
                name="modulusOfElasticity"
                control={control}
                rules={{ min: 0.001 }}
                render={({ field, fieldState }) => (
                  <FormField label={t.fields.modulusOfElasticity} error={fieldState.error}>
                    {({ labelId, isInvalid }) => (
                      <Numeric
                        id={labelId}
                        value={field.value ?? undefined}
                        min={0.001}
                        decimalScale={3}
                        isInvalid={isInvalid}
                        onValueChange={(value) => field.onChange(value)}
                      />
                    )}
                  </FormField>
                )}
              />
              <Controller
                name="isGlulam"
                control={control}
                render={({ field }) => (
                  <FormField label={t.fields.isGlulam}>
                    {({ labelId }) => <Switch id={labelId} checked={!!field.value} onChange={field.onChange} />}
                  </FormField>
                )}
              />
            </FormFieldsContainer>
          );
        }}
      </Form>
    </ModalDialog>
  );
};

export default TimberClassFormDialog;
